import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import HistoryItem from '../../components/HistoryItem/HistoryItem';
import PageHeader from '../../components/PageHeader';
import ListItemSkeleton from '../../components/Skeletons/ListItemSkeleton';
import AppConfig from '../../config';
import { usePageContext } from '../../hooks/usePageContext';
import { t } from '../../lang';
import HelperService from '../../services/HelperService';
import RequestService from '../../services/RequestService';

import './TransactionsHistoryPage.scss';

const FIRST_ITEM_MAP = 0;
const SECOND_ITEM_MAP = 1;
const COUNT_LOAD = 10;
const BILLING_ID = 8;

const sort = { by: 'Created', desc: true };
const skeletonGroup = [2, 3, 4];

const { routes } = AppConfig;

const TransactionsHistoryPage = () => {
	const obserfRef = useRef(null);
	const location = useLocation();
	const navigate = useNavigate();

	const currencyId = location?.state?.currencyId ?? 0;
	const addressId = location?.state?.addressId ?? 0;

	const [data, setData] = useState();
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(false);
	const [stop, setStop] = useState(false);

	const { useSearchParams } = usePageContext();
	const cardId = Number(useSearchParams().get('cardId'));

	const isBilling = currencyId === BILLING_ID;

	const changeData = resData => {
		if (resData.length === 0 || !resData) return setStop(true);

		setData(map => {
			const mapState = new Map(map);

			resData.forEach(i => {
				const key = isBilling ? i.Date.split('T')[0] : i.Created.split('T')[0];
				const value = i;

				if (!mapState.has(key)) {
					mapState.set(key, []);
				}

				mapState.get(key).push(value);
			});

			return mapState;
		});

		setPage(prev => prev + 1);
		setLoading(false);
	};

	const GetTransationByCurrency = async (newPage = page) => {
		const res = await RequestService.GetTransationByCurrency(
			currencyId,
			addressId,
			newPage,
			COUNT_LOAD,
			'',
			'',
			'',
			sort
		);

		res?.Transactions && changeData(res?.Transactions);
	};

	const getBillingTransaction = async () => {
		const res = await RequestService.BillingGetBalanceHistory({
			Page: page,
			Count: COUNT_LOAD,
		});

		res?.History &&
			changeData(
				res?.History.map(i => ({
					...i,
					TransactuinStatus: 2,
					TransactionType: i.Amount > 0 ? 1 : 2,
					AmountUsd: i.Amount,
					CurrencyId: BILLING_ID,
				}))
			);
	};

	const getCardTransaction = async () => {
		const res = await RequestService.GetCardHistory(cardId, page, COUNT_LOAD, sort);

		res?.Transactions && changeData(res?.Transactions);
	};

	useEffect(() => {
		const obserfRefCurrent = obserfRef.current;

		const observer = new IntersectionObserver(
			async ([entry]) => {
				if (entry.isIntersecting && !loading && !stop) {
					setLoading(true);
					cardId ? getCardTransaction() : isBilling ? getBillingTransaction() : GetTransationByCurrency();
				}
			},
			{
				root: null,
				rootMargin: '0px',
				threshold: 0.1,
			}
		);

		if (obserfRefCurrent) observer.observe(obserfRefCurrent);

		return () => {
			if (obserfRefCurrent) observer.unobserve(obserfRefCurrent);
		};
	}, [loading, stop]);

	const changeDate = date => {
		const getDate = new Date(date);
		const now = new Date();

		const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
		const target = Date.UTC(getDate.getUTCFullYear(), getDate.getUTCMonth(), getDate.getUTCDate());

		const diffDays = Math.floor((today - target) / (1000 * 60 * 60 * 24));

		if (diffDays === 0) return t('Today_uppercase');
		if (diffDays === 1) return t('Yesterday_uppercase');

		const dateFormate = HelperService.FormatDateMonthTextDayNumber(getDate);

		const day = Object.fromEntries(dateFormate.map(i => [i.type, i.value]));

		return `${day.month} ${day.day} / ${day.year}`;
	};

	const openTransaction = transaction =>
		!isBilling && navigate({ pathname: routes.transactionDetails }, { state: { transaction } });

	return (
		<div className='TransactionsHistoryPage'>
			<PageHeader header={'All transactions'} />
			{data &&
				Array.from(data.entries()).map((item, i) => (
					<div className='block-date' key={i}>
						<div className='date'>{changeDate(item[FIRST_ITEM_MAP])}</div>
						<div className='FormBlock'>
							{item[SECOND_ITEM_MAP].map((item, i) => (
								<HistoryItem key={i} transaction={item} onClick={openTransaction} />
							))}
						</div>
					</div>
				))}

			{!stop && <div ref={obserfRef} style={{ height: '20px' }}></div>}

			{!data && (
				<div className='TransactionsHistoryPage__skeleton'>
					{skeletonGroup.map((count, index) => (
						<div className='FormBlock' key={index}>
							{[...Array(count)].map((_, index) => (
								<ListItemSkeleton key={index} />
							))}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default TransactionsHistoryPage;
